<script>
import { mapGetters, mapMutations } from "vuex";
import Loader from "~/components/Loader";

const POLL_INTERVAL_MS = 2000;
const PAYMENT_TIMEOUT_MS = 20 * 60 * 1000;
const PAID_MODAL_LIFETIME_MS = 5000;

export default {
    name: "QRCodeModal",
    components: { Loader },
    data() {
        return {
            paid: false,
            timedOut: false,
            checking: false,
            pollTimer: null,
            paidCloseTimer: null,
            startedAt: 0,
        };
    },
    computed: {
        ...mapGetters({
            order: "order/getPaymentData",
            QRCodeModal: "order/getQRCodeModal",
        }),
    },
    mounted() {
        this.startPolling();
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        ...mapMutations("order", ["setQRCodeModal"]),
        startPolling() {
            this.stopPolling();
            this.paid = false;
            this.timedOut = false;
            this.startedAt = Date.now();
            this.pollTimer = setInterval(this.checkPayment, POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
            if (this.paidCloseTimer) {
                clearTimeout(this.paidCloseTimer);
                this.paidCloseTimer = null;
            }
        },
        close() {
            this.stopPolling();
            this.setQRCodeModal(null);
        },
        async checkPayment() {
            if (this.checking) return;
            const payment = this.QRCodeModal || {};
            if (Date.now() - this.startedAt >= PAYMENT_TIMEOUT_MS) {
                this.stopPolling();
                this.timedOut = true;
                return;
            }
            this.checking = true;
            try {
                let paid = false;

                try {
                    const { data } = await this.$axios.get("/api/order/check_payment", {
                        params: {
                            order_id: payment.orderId,
                            payment_id: payment.paymentId,
                        },
                    });
                    paid = data.is_paid === 1;
                } catch (error) {
                    // заказ не найден / временная ошибка — ждём следующего тика
                }
                if (paid) {
                    this.markPaid();
                }
            } finally {
                this.checking = false;
            }
        },
        markPaid() {
            this.stopPolling();
            const payment = this.QRCodeModal || {};
            // Яндекс.Метрика: ecommerce purchase — строго после подтверждённой оплаты.
            // Данные (products/coupon/shipping) стейджатся в OrderForm при создании QR —
            // здесь их собрать неоткуда: страница успеха делает clearOrder раньше.
            // Дедуп с пушем на странице успеха: общий sessionStorage-флаг по номеру заказа.
            try {
                const ymPurchaseKey = `ym_purchase_${payment.invoiceId}`;
                if (payment.invoiceId && !sessionStorage.getItem(ymPurchaseKey)) {
                    sessionStorage.setItem(ymPurchaseKey, "1");
                    this.$grom.reachEcommerce("purchase", {
                        actionField: {
                            id: payment.invoiceId, // Идентификатор заказа. Обязательный параметр.
                            affiliation: "Post Post Scriptum", // Название магазина
                            revenue: Number(payment.amount).toFixed(2), // Финальная стоимость заказа, копейки - после точки
                            shipping: isNaN(parseInt(payment.shipping)) ? "" : payment.shipping, // Стоимость доставки
                            coupon: payment.coupon,
                        },
                        products: payment.products || [],
                    });
                }
            } catch (e) {
                console.log(`e ym purchase =>`, e);
            }
            this.paid = true;
            this.paidCloseTimer = setTimeout(this.close, PAID_MODAL_LIFETIME_MS);
        },
    },
};
</script>

<template>
    <div class="qr-code-modal text_13" v-bsl="true">
        <div class="qr-code-modal__content">
            <div class="qr-code-modal__close" @click="close">{{ $t("close") }}</div>

            <template v-if="!paid && !timedOut">
                <img v-lazy-load :data-src="`data:image/png;base64,${QRCodeModal.base64}`" alt="" />
                <div class="flex-c qr-code-modal__loader">
                    <Loader inline></Loader>
                    <span>{{ $t("await_payment") }}</span>
                </div>
            </template>

            <div v-else-if="paid" class="qr-code-modal__paid">
                <div class="qr-code-modal__paid-icon">✓</div>
                <span>Оплачено</span>
            </div>

            <div v-else class="qr-code-modal__timeout">
                <span>
                    Не удалось подтвердить оплату. Если вы оплатили заказ, он будет обработан в течение нескольких
                    минут.
                </span>
                <div class="button text_13" @click="close">Закрыть</div>
            </div>

            <div v-if="!paid && !timedOut" class="button text_13" @click="close">
                {{ $t("cancel") }}
            </div>
        </div>
    </div>
</template>

<style>
.qr-code-modal {
    position: fixed;
    z-index: 999;
    inset: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: grid;
    place-items: center;
    background: rgba(0, 0, 0, 0.267);
}
.qr-code-modal__close {
    position: absolute;
    right: 16px;
    top: 16px;
}
.qr-code-modal__content {
    padding: 32px 16px 16px;
    position: relative;
    width: 332px;
    max-width: 90%;
    max-height: 90%;
    background: #fff;
}

.qr-code-modal__loader {
    gap: 16px;
    margin-top: 16px;
    margin-bottom: 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.qr-code-modal__paid {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: 32px;
}

.qr-code-modal__paid-icon {
    display: grid;
    place-items: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #2e7d32;
    color: #fff;
    font-size: 32px;
}

.qr-code-modal__timeout {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: 32px;
    text-align: center;
}
</style>
